// [SERVEUR-1] Page de prise de commande pour le rôle serveur
import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Plus, Minus, X, Send, Loader2, ClipboardList,
  UtensilsCrossed, Check, Clock, XCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import ProductPhoto from "../components/ProductPhoto";
import { VariantDialog } from "../components/sales/SalesDialogs";
import { getFeatures } from "../lib/boutiqueType";
import { useSearchParams, Link } from "react-router-dom";
import { Banknote, Smartphone } from "lucide-react";
import { enabledOperators } from "../lib/payments";

const STATUS_CONFIG = {
  en_attente: { label: "En attente", color: "bg-yellow-500", icon: Clock },
  encaissee: { label: "Encaissée", color: "bg-green-600", icon: Check },
  annulee: { label: "Annulée", color: "bg-red-600", icon: XCircle },
};

export default function ServiceOrders() {
  const { getAuthHeaders } = useAuth();
  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || "€";
  const primaryColor = boutiqueData.customColor || "#FF4500";
  const features = getFeatures();
  const currentUser = (() => {
    try { return JSON.parse(sessionStorage.getItem("currentUser") || "{}"); }
    catch { return {}; }
  })();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [searchParams] = useSearchParams();
  const [tableName, setTableName] = useState(searchParams.get("name") || "");
  const [tableId, setTableId] = useState(searchParams.get("table") || "");
  const [tables, setTables] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  // [QR-TABLES] intention de paiement relevée à table + confirmation du client
  const [payMethod, setPayMethod] = useState(null);
  const [payOperator, setPayOperator] = useState(null);
  const [clientConfirmed, setClientConfirmed] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/categories`, getAuthHeaders())
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/table-orders?mine=true`, getAuthHeaders());
      setMyOrders(res.data.orders || []);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    fetchMyOrders();
    const interval = setInterval(fetchMyOrders, 20000);
    return () => clearInterval(interval);
  }, [fetchMyOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "all" || p.category_id === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const addToCart = (product, variant) => {
    const tracked = product.track_stock !== false;
    const cartKey = `${product.id}-${variant.id}`;
    setCart((prev) => {
      const existing = prev.find((i) => i.cart_key === cartKey);
      if (existing) {
        if (tracked && existing.quantity >= variant.quantity) {
          toast.error("Stock insuffisant");
          return prev;
        }
        return prev.map((i) => i.cart_key === cartKey
          ? { ...i, quantity: i.quantity + 1, total: (i.quantity + 1) * i.unit_price }
          : i);
      }
      return [...prev, {
        cart_key: cartKey,
        product_id: product.id,
        variant_id: variant.id,
        product_name: product.name,
        size: variant.size,
        color: variant.color,
        quantity: 1,
        unit_price: product.sale_price,
        total: product.sale_price,
        max_quantity: tracked ? variant.quantity : 999999
      }];
    });
    setVariantDialogOpen(false);
    toast.success(`✓ ${product.name} ajouté`, { duration: 1200 });
  };

  const handleProductClick = (product) => {
    if (product.track_stock === false && product.variants?.[0]) { addToCart(product, product.variants[0]); return; }
    const available = (product.variants || []).filter((v) => v.quantity > 0);
    if (available.length === 0) { toast.error("Rupture de stock"); return; }
    if (available.length === 1) { addToCart(product, available[0]); return; }
    setSelectedProduct(product);
    setSelectedVariant(null);
    setVariantDialogOpen(true);
  };

  const updateQty = (cartKey, delta) => {
    setCart((prev) => prev.map((i) => {
      if (i.cart_key !== cartKey) return i;
      const q = i.quantity + delta;
      if (q <= 0) return i;
      if (q > i.max_quantity) { toast.error("Stock insuffisant"); return i; }
      return { ...i, quantity: q, total: q * i.unit_price };
    }));
  };

  const cartTotal = cart.reduce((s, i) => s + i.total, 0);
  const formatPrice = (p) => `${p.toLocaleString("fr-FR")} ${devise}`;

  useEffect(() => {
    axios.get(`${API}/tables`, getAuthHeaders()).then((r) => setTables(r.data.tables.filter((t) => !t.virtual))).catch(() => {});
    axios.get(`${API}/settings`, getAuthHeaders()).then((r) => setStoreSettings(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickTable = (t) => { setTableId(t.id); setTableName(t.name); };

  const handleSendOrder = async () => {
    if (!tableName.trim()) { toast.error("Choisissez la table"); return; }
    if (cart.length === 0) { toast.error("La commande est vide"); return; }
    if (payMethod === "card" && !payOperator) { toast.error("Précisez l'opérateur Mobile Money"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/table-orders`, {
        table_name: tableName.trim(),
        table_id: tableId || null,
        note: note || null,
        payment_method: payMethod,
        payment_operator: payMethod === "card" ? payOperator : null,
        client_confirmed: clientConfirmed && !!payMethod,
        items: cart.map(({ cart_key, max_quantity, ...item }) => item)
      }, getAuthHeaders());
      toast.success("🍽️ Commande envoyée !");
      setCart([]);
      setTableName("");
      setTableId("");
      setPayMethod(null); setPayOperator(null); setClientConfirmed(false);
      setNote("");
      fetchMyOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-24" data-testid="service-orders-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-outfit flex items-center gap-2">
          <UtensilsCrossed className="w-6 h-6" style={{ color: primaryColor }} />
          Prise de commande
        </h1>
        <p className="text-muted-foreground">
          {currentUser.prenom ? `Serveur : ${currentUser.prenom}` : "Prenez les commandes des clients"}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Catalogue */}
        <div className="lg:col-span-2 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher un article..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="pl-10"
              data-testid="service-search-input" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setActiveCategory("all")}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              style={activeCategory === "all"
                ? { backgroundColor: primaryColor, color: "#fff" }
                : { backgroundColor: `${primaryColor}15`, border: `1px solid ${primaryColor}30` }}>
              Tous
            </button>
            {categories.map((cat) => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={activeCategory === cat.id
                  ? { backgroundColor: primaryColor, color: "#fff" }
                  : { backgroundColor: `${primaryColor}15`, border: `1px solid ${primaryColor}30` }}>
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {filteredProducts.map((product) => {
              const tracked = product.track_stock !== false;
              const stock = product.total_quantity || 0;
              const soldOut = tracked && stock === 0;
              return (
                <div key={product.id} onClick={() => !soldOut && handleProductClick(product)}
                  className={`rounded-lg overflow-hidden border bg-card cursor-pointer transition-all hover:shadow-md ${soldOut ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`service-product-${product.id}`}>
                  <div className="relative" style={{ aspectRatio: "4/3" }}>
                    <ProductPhoto productId={product.id} backendImage={product.image}
                      categoryName={product.category_name} categoryIcon={product.category_icon}
                      size="full" aspectRatio="4/3" className="w-full h-full object-cover" />
                    {tracked && (
                    <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px]">
                      {stock}
                    </span>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-semibold truncate">{product.name}</p>
                    <p className="text-xs font-bold mt-0.5">{formatPrice(product.sale_price)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Commande en cours */}
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <ClipboardList className="w-4 h-4" /> Commande en cours
              {cart.length > 0 && <Badge style={{ backgroundColor: primaryColor }} className="text-white">{cart.length}</Badge>}
            </h3>
            <div className="space-y-2">
              <Label className="text-xs">Table *</Label>
              {tables.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="service-table-picker">
                  {tables.map((t) => (
                    <button key={t.id} type="button" onClick={() => pickTable(t)}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-colors"
                      style={{ borderColor: tableId === t.id ? primaryColor : (t.state === "libre" ? "#22C55E55" : "#FACC1555"), background: tableId === t.id ? primaryColor : "transparent", color: tableId === t.id ? "#fff" : undefined }}
                      data-testid={`service-table-${t.id}`}>
                      {t.name}{t.state !== "libre" ? " •" : ""}
                    </button>
                  ))}
                </div>
              )}
              <Input value={tableName} onChange={(e) => { setTableName(e.target.value); setTableId(""); }}
                placeholder={tables.length ? "…ou nom du client" : "Ex: Table 5 ou M. Kouadio"} data-testid="service-table-input" />
              {tables.length === 0 && <Link to="/admin/tables" className="text-[11px] underline text-muted-foreground flex items-center gap-1"><UtensilsCrossed className="w-3 h-3" /> Créer les tables et QR codes</Link>}
            </div>
            {cart.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                Touchez un article pour l'ajouter
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {cart.map((item) => (
                  <div key={item.cart_key} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50 text-xs"
                    data-testid={`service-cart-item-${item.cart_key}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.product_name}</p>
                      {features.variants && <p className="text-muted-foreground">{item.size}{item.color ? ` · ${item.color}` : ""}</p>}
                    </div>
                    <button onClick={() => updateQty(item.cart_key, -1)} className="w-5 h-5 rounded border bg-card flex items-center justify-center">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-5 text-center font-medium">{item.quantity}</span>
                    <button onClick={() => updateQty(item.cart_key, 1)} className="w-5 h-5 rounded border bg-card flex items-center justify-center">
                      <Plus className="w-3 h-3" />
                    </button>
                    <span className="font-semibold w-16 text-right">{formatPrice(item.total)}</span>
                    <button onClick={() => setCart((p) => p.filter((i) => i.cart_key !== item.cart_key))}
                      className="text-muted-foreground hover:text-destructive">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optionnel)" className="text-xs" data-testid="service-note-input" />
            </div>

            {/* [QR-TABLES] Le client règle comment ? (optionnel, à demander à table) */}
            <div className="space-y-2" data-testid="service-payment-intent">
              <Label className="text-xs">Le client règle en… <span className="text-muted-foreground">(optionnel)</span></Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => { setPayMethod(payMethod === "cash" ? null : "cash"); setPayOperator(null); }}
                  className="py-2 rounded-lg text-white text-xs font-bold flex items-center justify-center gap-1 border-2"
                  style={{ background: "#111", borderColor: payMethod === "cash" ? "#FF4500" : "#2A2A2A" }} data-testid="service-pay-cash"><Banknote className="w-4 h-4" /> Espèces</button>
                <button type="button" onClick={() => setPayMethod(payMethod === "card" ? null : "card")}
                  className="py-2 rounded-lg text-white text-xs font-bold flex items-center justify-center gap-1 border-2"
                  style={{ background: payMethod === "card" ? "#FF4500" : "#22C55E", borderColor: payMethod === "card" ? "#FF4500" : "#22C55E" }} data-testid="service-pay-card"><Smartphone className="w-4 h-4" /> Mobile Money</button>
              </div>
              {payMethod === "card" && (
                <div className="grid grid-cols-4 gap-1.5">
                  {enabledOperators(storeSettings).map((op) => (
                    <button key={op.id} type="button" onClick={() => setPayOperator(op.id)} className="py-1.5 rounded-lg text-[10px] font-bold border-2"
                      style={{ background: payOperator === op.id ? "#FF4500" : op.color, color: payOperator === op.id ? "#fff" : op.text, borderColor: payOperator === op.id ? "#fff" : op.color }}
                      data-testid={`service-op-${op.id}`}>{op.short}</button>
                  ))}
                </div>
              )}
              {payMethod && (
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="service-client-confirmed">
                  <input type="checkbox" checked={clientConfirmed} onChange={(e) => setClientConfirmed(e.target.checked)} className="w-4 h-4 accent-[#FF4500]" />
                  Le client a confirmé ce mode de paiement
                </label>
              )}
            </div>
            <div className="flex justify-between font-bold border-t pt-2">
              <span>TOTAL</span>
              <span>{formatPrice(cartTotal)}</span>
            </div>
            <Button onClick={handleSendOrder} disabled={sending || cart.length === 0}
              className="w-full text-white" style={{ backgroundColor: primaryColor }}
              data-testid="service-send-order-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer la commande
            </Button>
          </Card>

          {/* Mes commandes du jour */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold text-sm">Mes commandes du jour</h3>
            {myOrders.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">Aucune commande aujourd'hui</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {myOrders.map((order) => {
                  const st = STATUS_CONFIG[order.status] || STATUS_CONFIG.en_attente;
                  return (
                    <div key={order.id} className="p-2 rounded-lg border text-xs" data-testid={`my-order-${order.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{order.table_name}</span>
                        <Badge className={`${st.color} text-white text-[10px]`}>{st.label}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 truncate">
                        {order.items.map((i) => `${i.quantity}× ${i.product_name}`).join(", ")}
                      </p>
                      <p className="font-bold mt-1">{formatPrice(order.total)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      <VariantDialog
        open={variantDialogOpen} onOpenChange={setVariantDialogOpen}
        selectedProduct={selectedProduct} selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant} primaryColor={primaryColor}
        onConfirm={() => selectedProduct && selectedVariant && addToCart(selectedProduct, selectedVariant)}
      />
    </div>
  );
}
